import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Hiện dần một khối khi nó cuộn vào màn hình — dùng cho các khối của trang
 * giới thiệu.
 *
 * Trạng thái ẩn (`opacity: 0` + đẩy xuống 14px) nằm TRONG media query
 * `prefers-reduced-motion: no-preference` ở `index.css`, không nằm ở đây: người
 * tắt animation phải thấy nội dung ngay lập tức, kể cả khi JS chưa kịp chạy
 * hay IntersectionObserver không bắn. Vắng media query đó thì `.reveal` không
 * làm gì cả — an toàn mặc định.
 *
 * `visible` là state chứ không phải `classList.add` trực tiếp: React sẽ ghi đè
 * thuộc tính class về đúng giá trị prop ở lần render sau (ví dụ khi query của
 * PublicStats trả dữ liệu mới), và một class gắn tay sẽ biến mất giữa chừng.
 */
export function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: ReactNode;
  /** Trễ thêm (ms) — dùng để các thẻ trong cùng một lưới hiện so le. */
  delay?: number;
  className?: string;
}): React.JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting === true) {
          setVisible(true);
          io.disconnect();
        }
      },
      // -40px đáy: khối phải vào hẳn màn hình một đoạn rồi mới hiện, không
      // lấp ló ở mép là đã chạy xong animation ngoài tầm nhìn.
      { threshold: 0.15, rootMargin: '0px 0px -40px' },
    );
    io.observe(el);
    return () => { io.disconnect(); };
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`}
      style={delay > 0 ? { transitionDelay: `${String(delay)}ms` } : undefined}
    >
      {children}
    </div>
  );
}
